import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { DashboardLayout, Logo, useCan, useIfUser, useSetBrand, type NavSection, type RootNavItem } from '@if/mui'
import { access } from '@src/access'
import { APP_VERSION } from '@src/appVersion'
import { getNewApplicationHref } from '@src/components/AddApplication'
import { useInvitationDialog } from '@src/containers/BrowseCreditApplicationPage/components/InvitationDialog'
import { useCanInviteApplicant } from '@src/hooks/invitation-hooks'
import { useReportSettingsDialog } from '@src/containers/ReportSettingsPage/ReportSettingsDialog'
import { useUnreadEntries } from '@src/data/api/chat-message/chat-message-api'
import { useNewApplicationPrograms } from '@src/data/merchant-config-selector'
import { useChatMessageSignalR } from '@src/hooks/useChatMessageSignalR'
import { FinancingCompanyConfigs } from '@src/types/Constants'
import {
  BarChart3,
  BookOpen,
  Calculator,
  House,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings,
  Settings2,
  UserPlus,
  Users,
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import AlertBannerOnDeprecatedStatus from './components/AlertBannerOnDeprecatedStatus'
import AlertBannerOnProduction from './components/AlertBannerOnProduction'
import AppBarSupport from './components/AppBarSupport'
import { useEditProfileDialog } from './components/EditProfileDialog'
import { useResourcesDialog } from './components/ResourcesDialog'
import { useMerchant } from './MerchantProvider'

const favIconLink = document.querySelector<HTMLLinkElement>("link[rel~='icon']")

/**
 * The app shell: the @if/mui DashboardLayout (responsive sidebar + app bar)
 * replacing the old AppHeader/TemplateApp. The app bar provides the language
 * toggle, light/dark toggle and profile menu (with logout); the endSlot carries
 * the contact-us line. Each nav item is shown only when the user can actually
 * reach its page — matching the route guards — so the sidebar never offers a
 * link that just bounces home.
 */
const DashboardShell = () => {
  const { t, i18n } = useTranslation()
  const user = useIfUser()
  const merchant = useMerchant()
  const canCreateApplications = useCan(access.createApplication)
  const canSendWelcomeMessage = useCanInviteApplicant()
  const canManageUsers = useCan(access.manageUsers)
  const canSetReportSettings = useCan(access.reportSettings, { merchant })
  const canUseMessaging = useCan(access.messaging)
  const canUseIFXProgram = useCan(access.ifxFinancingProgram)
  const canUseCalculator = useCan(access.loanCalculator, { merchant })
  const canUseCustomBuydown = useCan(access.customBuydown, { merchant })
  const canUseInsights = useCan(access.merchantStatistics, { merchant })
  const [unreadEntries] = useUnreadEntries()

  // Edit-profile dialog, opened imperatively from the app-bar profile menu.
  const editProfileDialog = useEditProfileDialog()

  // Shared Invite Applicant dialog, opened imperatively from the sidebar action
  // item (below) and the home hero button.
  const invite = useInvitationDialog()

  // Report Settings dialog, opened from the parameters-section action item below
  // (replacing the old /report-settings route).
  const reportSettings = useReportSettingsDialog()

  const company = merchant?.financingCompanyId ?? EFinancingCompany.IFinance
  const config = FinancingCompanyConfigs[company]
  const totalUnread = unreadEntries.length

  // Skin the @if/mui theme to the logged-in merchant's company (accent, chrome,
  // headers) — the theme counterpart to the per-company favicon below.
  const setBrand = useSetBrand()
  useEffect(() => {
    setBrand(company)
  }, [company, setBrand])

  // Keep real-time unread chat updates flowing (parity with the old header).
  useChatMessageSignalR({
    merchantId: user.merchantId,
    enabled: canUseMessaging && !!user.merchantId,
  })

  useEffect(() => {
    if (favIconLink && config.favicon) favIconLink.href = config.favicon
  }, [config.favicon])

  const newAppPrograms = useNewApplicationPrograms(merchant)
  const resourcesDialog = useResourcesDialog(merchant)
  const newApplicationHref = (program: EFinancingProgram) => getNewApplicationHref(program, canUseIFXProgram)
  const newApplicationItem: RootNavItem | null =
    !canCreateApplications || newAppPrograms.length === 0
      ? null
      : newAppPrograms.length === 1
        ? { to: newApplicationHref(newAppPrograms[0]), label: t('nav.newApplication'), icon: Plus }
        : {
            label: t('nav.newApplication'),
            icon: Plus,
            children: newAppPrograms.map((program) => ({
              to: newApplicationHref(program),
              label: t(`EFinancingProgram.${program}`, { ns: 'enum' }),
            })),
          }

  const resourcesItem: RootNavItem | null =
    resourcesDialog.resources.length === 0
      ? null
      : { onClick: () => void resourcesDialog.open(), label: t('nav.resources'), icon: BookOpen }

  const mainItems: RootNavItem[] = [
    { to: '/', label: t('nav.home'), icon: House },
    ...(newApplicationItem ? [newApplicationItem] : []),
    ...(canUseMessaging
      ? [{ to: '/chat-dashboard', label: t('nav.messages'), icon: MessageSquare, badge: totalUnread || undefined }]
      : []),
    { to: '/credits', label: t('nav.applications'), icon: LayoutDashboard, end: true },
    ...(canUseCalculator ? [{ to: '/loan-calculator', label: t('nav.calculator'), icon: Calculator }] : []),
    ...(canUseCustomBuydown ? [{ to: '/custom-buydown', label: t('nav.paymentPlanBuilder'), icon: Settings2 }] : []),
    ...(canUseInsights ? [{ to: '/merchant-statistics', label: t('nav.insights'), icon: BarChart3 }] : []),
    ...(resourcesItem ? [resourcesItem] : []),
  ]

  const quickActions: RootNavItem[] = canSendWelcomeMessage
    ? [
        {
          onClick: () =>
            void invite.open({
              financingCompanyId: merchant.financingCompanyId,
              loanPurposeId: merchant.serviceCategory ?? null,
            }),
          label: t('nav.inviteApplicant'),
          icon: UserPlus,
        },
      ]
    : []

  const parametersItems: RootNavItem[] = [
    ...(canManageUsers ? [{ to: '/manage-users', label: t('nav.manageUsers'), icon: Users }] : []),
    ...(canSetReportSettings
      ? [{ onClick: () => void reportSettings.open(), label: t('nav.reportSettings'), icon: Settings }]
      : []),
  ]

  const nav: NavSection[] = [
    { items: mainItems },
    ...(quickActions.length ? [{ title: t('nav.sections.quickAction'), items: quickActions }] : []),
    ...(parametersItems.length ? [{ title: t('nav.sections.parameters'), items: parametersItems }] : []),
  ]

  return (
    <DashboardLayout
      logo={<Logo company={company} background="dark" />}
      nav={nav}
      appTitle={t('app.title')}
      version={APP_VERSION}
      onProfile={{ onProfileAction: () => void editProfileDialog.edit() }}
      endSlot={
        <AppBarSupport
          showMessages={canUseMessaging}
          messagesHref="/chat-dashboard"
          unreadCount={totalUnread}
          phone={config.phone}
          email={config.email}
          faqUrl={i18n.resolvedLanguage === 'fr' ? config.faqUrlFR : config.faqUrlEN}
        />
      }
    >
      <AlertBannerOnProduction />
      <AlertBannerOnDeprecatedStatus />
      <Outlet />
    </DashboardLayout>
  )
}

export default DashboardShell
